/**
 * Messwerteingabe für die Beurteilung der Hell/Dunkel-Grenze Scheinwerer-Einstellgerät
 */

import { observable, action } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';
import { bind } from '../../../utils/decorators';
import { FormControl, FormGroup, Table, Row, Col, Checkbox } from 'react-bootstrap';
import VorgangFormularElement from '../VorgangFormularElement';
import { round } from '../../../utils/round';

class Messpunkt {
  editable: boolean;
  soll: number;
  winkel: string;
  id: string;
  @observable messwert: number | undefined;
  @observable abweichung: number | undefined;
  @observable abweichungIo: boolean = false;
  constructor(editable: boolean, soll: number, winkel: string, id: string, messwert: number | undefined) {
    this.editable = editable;
    this.messwert = messwert;
    this.soll = soll;
    this.winkel = winkel;
    this.id = id;
  }

  public setMesswert(messwert: number | undefined) {
    this.messwert = messwert;
    if ((this.messwert !== undefined) && (!isNaN(this.messwert))) {
      this.abweichung = round(this.soll - this.messwert, 2);
      // Abweichung muss innerhalb von +/-0,2% liegen
      this.abweichungIo =  (Math.abs(this.abweichung) <= 0.2);
    } else {
      this.abweichung = undefined;
      this.abweichungIo = false;
    }
  }
}

class Messung {
  nummer: number;
  @observable messpunkte: Array<Messpunkt>;
  constructor(nummer: number) {
    this.nummer = nummer;
  }
}
export interface ISepHellDunkelGrenze {
  typ: string;
  elementguid: string;
  kategorieguid: string;
  editable?: boolean;
  value?: string;
  element: VorgangFormularElement;
  onHandleChange?: (e: VorgangFormularElement) => void;
}

export interface ISepHellDunkelGrenzeState {
  justage: boolean;
  messungen: {
    vorjustage: Messung[];
    nachjustage: Messung[];
  };
}

@observer
export default class SepHellDunkelGrenze extends React.Component<ISepHellDunkelGrenze, ISepHellDunkelGrenzeState> {

    @observable validationstate: 'success' | 'warning' | 'error' | undefined = 'error';

    public messPunktePkw: number[] = [ -0, -1, -2 ];
    public messWinkelPkw: string[] = [ '0', '0,57', '1,15' ];

    public messPunkteLkw: number[] = [ -0, -2, -4 ];
    public messWinkelLkw: string[] = [ '0', '1,15', '2,29' ];

    private anzahlMessungen: number = 3;

    @observable public typ: string = this.props.typ;
    @observable public justage: boolean;
    @observable public abweichung: number[][];

    daten: Array<string> = new Array<string>();

    constructor(props: ISepHellDunkelGrenze) {
      super(props);
      this.state = { justage: false, messungen: { vorjustage: Array<Messung>(), nachjustage: Array<Messung>() } };
      if (this.props.value) {
        this.daten = JSON.parse(this.props.value);
        // Zwei komplette Datensätze = Justage wurde durchgeführt
        this.justage = (this.daten.length > 1);
      }

      // Messpunkte vor und nach Justage erzeugen
      let sollwerte: number[] = (this.props.typ === 'LKW') ? this.messPunkteLkw : this.messPunktePkw;
      let winkel: string[] = (this.props.typ === 'LKW') ? this.messWinkelLkw : this.messWinkelPkw;
      for (let i = 0; i < this.anzahlMessungen; i++) {
        for (let j = 0; j < 2; j++) {
          const messung: Messung = new Messung(i);
          messung.messpunkte = new Array<Messpunkt>();

          for (let k = 0; k < sollwerte.length; k++) {
            let index = (i % 2 === 0) ? k : (sollwerte.length - 1 - k);

            // Formular-ID die vom Back-End zur Übernahme der Daten genutzt wird
            const id: string = `${this.props.kategorieguid}[${this.props.elementguid}][${j}][${i * 3 + k}]`;
            const messpunkt: Messpunkt = new Messpunkt(this.props.editable ? this.props.editable : true, sollwerte[index], winkel[index], id, undefined);

            // Werte aus Datenübernahme
            if ((this.daten) && (this.daten.length > j) && (this.daten[j][i * 3 + k][1] !== null)) {
              const wert: number = parseFloat(this.daten[j][i * 3 + k][1]);
              messpunkt.setMesswert(wert);
            }

            messung.messpunkte.push(messpunkt);
          }

          j === 0 ? this.state.messungen.vorjustage.push(messung) : this.state.messungen.nachjustage.push(messung);
        }
      }

      this.validate();
    }

    private getMesspunktValidationState(p: Messpunkt, vorjustage: boolean) {
      let res: 'success' | 'warning' | 'error' | undefined;
      res = p.messwert !== undefined ? undefined : 'error';
      // Bei Justage kann die Eingabe im ersten Durchgang vorzeitig abgebrochen werden, dann soll keine Validierung angzeigt werden
      if (vorjustage && this.justage) {
        res = undefined;
      }
      return res;
    }

    public renderMessungen(messungen: Array<Messung>, vorjustage: boolean) {
      return(
        messungen.map((messung, indexMessung) => (
          <Row key={`${indexMessung}`}>
            <Col xs={2}/>
            <Col xs={8}>
              <Table striped={true} bordered={true} condensed={true} hover={true}>
                <thead>
                  <tr>
                      <th colSpan={3}>{`Messung ${indexMessung}`}</th>
                  </tr>
                  <tr>
                      <th>Soll [%]</th>
                      <th>Messwert ist [%]</th>
                      <th>Abweichung</th>
                  </tr>
                </thead>
                <tbody>
                  {messung.messpunkte.map((p, indexPunkt) =>
                    <tr key={`${indexMessung}-${indexPunkt}`}>
                      <td className="tabelle-text-zentriert">{`${p.soll} (${p.winkel}°)`}</td>
                      <td>
                        <FormGroup validationState={this.getMesspunktValidationState(p, vorjustage)} style={{margin: '0px'}}>
                          <FormControl readOnly={!this.props.editable} step={0.01} onChange={(e) => this.handleMesswertChange(p, e)} type="number" name={`${p.id}[1]`} value={p.messwert}/>
                          <FormControl type="hidden" name={`${p.id}[2]`} value={p.abweichung}/>
                          <FormControl type="hidden" name={`${p.id}[0]`} value={`${p.soll} (${p.winkel}°)`}/>
                        </FormGroup>
                      </td>
                      <td className="tabelle-text-zentriert">
                        <span className={p.abweichungIo ? 'text-success' : 'text-danger'}>{(p.abweichung !== undefined) ? `${p.abweichung.toLocaleString('de-DE')} %` : '-'}</span>
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </Col>
            <Col xs={2}/>
          </Row>
        ))
      );
    }

    @bind @action handleJustageChange(event: React.FormEvent<FormControl>) {
      this.justage = (event.target as HTMLInputElement).checked;
      this.validate();
      if (this.props.onHandleChange) {
        this.props.onHandleChange(this.props.element);
      }
    }

    @bind @action handleMesswertChange(messpunkt: Messpunkt, ev: React.FormEvent<FormControl>) {
      messpunkt.messwert = (ev.target as HTMLInputElement).value !== '' ? (ev.target as HTMLInputElement).valueAsNumber : undefined;
      if (messpunkt.messwert !== undefined) {
        messpunkt.abweichung = round(messpunkt.soll - messpunkt.messwert, 2);
        messpunkt.abweichungIo =  (Math.abs(messpunkt.abweichung) <= 0.2);
      } else {
        messpunkt.abweichungIo = false;
      }
      this.validate();
      if (this.props.onHandleChange) {
        this.props.onHandleChange(this.props.element);
      }
    }

    public validate() {
      this.validationstate = 'success';
      // Messpunkte vor Justage nur validieren wenn keine Justage gewählt wurde
      if (!this.justage) {
        for (let messung of this.state.messungen.vorjustage) {
          for (let messpunkt of messung.messpunkte) {
            if (messpunkt.messwert === undefined) {
              this.validationstate = 'error';
              break;
            }
          }
        }
      }
      if (this.justage) {
        for (let messung of this.state.messungen.nachjustage) {
          for (let messpunkt of messung.messpunkte) {
            if (messpunkt.messwert === undefined) {
              this.validationstate = 'error';
              break;
            }
          }
        }
      }
      this.props.element.validationstate = this.validationstate;
    }

     public render() {
        return(
          <div>
            <div>
              {this.renderMessungen(this.state.messungen.vorjustage, true)}
              <div style={{textAlign: 'center', padding: '10px'}}>
                {this.props.editable ? <Checkbox onChange={this.handleJustageChange} inline={true} defaultChecked={this.justage}>Justage notwendig</Checkbox> : 'Werte nach Justage'}
              </div>
              {this.justage && this.renderMessungen(this.state.messungen.nachjustage, false)}
            </div>
          </div>
        );
    }
}



// WEBPACK FOOTER //
// ./~/tslint-loader!./src/components/vorlagen/ses/SepHellDunkelGrenze.tsx